import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import url from '../constants/config';

export const emptyDraft = text => !text || !text.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;|\u00a0/g, ' ').trim();
export const draftChanged = () => window.dispatchEvent(new Event('message-draft-changed'));

export default function useMessageDraft(type, id, identity) {
  const [text, setText] = useState('');
  const [status, setStatus] = useState('loading');
  const state = useRef({ text: '', revision: 0, dirty: false, loaded: false, conflict: false, pending: null });
  const mounted = useRef(true);
  const timer = useRef(null);
  // Capture credentials per editor instance, so a logout/account switch cannot redirect an old save.
  const client = useRef(axios.create({ headers: { Authorization: `Bearer ${window.localStorage.getItem('authorization')}` } })).current;
  const endpoint = `${url}/message-drafts/${type}/${id}`;
  const cacheKey = `message-draft:v1:${encodeURIComponent(identity)}:${type}:${id}`;
  const showStatus = value => { if (mounted.current) setStatus(value); };
  const cache = () => {
    try {
      window.localStorage.setItem(cacheKey, JSON.stringify({ text: state.current.text, revision: state.current.revision }));
    } catch { /* Server autosave still works when browser storage is unavailable. */ }
  };
  const clearCache = () => { try { window.localStorage.removeItem(cacheKey); } catch { /* No storage. */ } };

  const save = async () => {
    const s = state.current;
    if (!s.loaded || s.conflict) return false;
    if (s.pending) return s.pending;
    if (!s.dirty) return true;
    s.pending = (async () => {
      try {
        while (s.dirty && !s.conflict) {
          const snapshot = s.text;
          showStatus('saving');
          const response = await client.put(endpoint, { text: snapshot, revision: s.revision });
          s.revision = response.data.revision;
          if (s.text === snapshot) { s.dirty = false; clearCache(); }
          else cache();
          draftChanged();
        }
        showStatus('saved');
        return true;
      } catch (error) {
        s.conflict = error.response?.status === 409;
        showStatus(s.conflict ? 'conflict' : 'error');
        return false;
      } finally { s.pending = null; }
    })();
    return s.pending;
  };

  const load = async (recover = false) => {
    const startedRevision = state.current.revision;
    const startedText = state.current.text;
    try {
      const response = await client.get(endpoint);
      if (!mounted.current) return;
      const s = state.current;
      if (!recover && s.loaded && (s.dirty || s.pending || s.conflict)) return;
      // A slow read must not replace an edit, save or deletion completed after it started.
      if (s.revision !== startedRevision || s.text !== startedText || response.data.revision < s.revision) return;
      const remoteText = response.data.draft?.text || '';
      s.revision = response.data.revision;
      s.text = remoteText;
      s.loaded = true;
      s.dirty = false;
      s.conflict = false;
      if (recover) {
        let stored;
        try { stored = JSON.parse(window.localStorage.getItem(cacheKey)); } catch { stored = null; }
        if (stored && typeof stored.text === 'string' && stored.text !== remoteText) {
          s.text = stored.text;
          s.dirty = true;
          s.conflict = stored.revision !== s.revision;
        } else clearCache();
      }
      setText(s.text);
      showStatus(s.conflict ? 'conflict' : 'saved');
      if (s.dirty && !s.conflict) await save();
    } catch { showStatus('load-error'); }
  };

  useEffect(() => {
    const editorState = state.current;
    mounted.current = true;
    load(true);
    const poll = setInterval(() => load(), 15000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      mounted.current = false;
      clearInterval(poll);
      clearTimeout(timer.current);
      window.removeEventListener('focus', onFocus);
      // Browser cache is synchronous; an in-flight save or this flush persists normal navigation.
      if (editorState.dirty && !editorState.conflict) save();
    };
    // The parent keys each editor by account and conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = value => {
    const s = state.current;
    if (!s.loaded) return;
    const next = emptyDraft(value) ? '' : value;
    if (next === s.text) return;
    s.text = next;
    s.dirty = true;
    setText(next);
    cache();
    showStatus(s.conflict ? 'conflict' : 'unsaved');
    clearTimeout(timer.current);
    timer.current = setTimeout(save, 400);
  };

  const discard = async () => {
    clearTimeout(timer.current);
    if (state.current.pending) await state.current.pending;
    if (state.current.conflict) return false;
    try {
      const response = await client.delete(endpoint, { data: { revision: state.current.revision } });
      Object.assign(state.current, { text: '', revision: response.data.revision, dirty: false });
      setText('');
      clearCache();
      showStatus('saved');
      draftChanged();
      return true;
    } catch (error) {
      state.current.conflict = error.response?.status === 409;
      showStatus(state.current.conflict ? 'conflict' : 'error');
      return false;
    }
  };

  const sent = async () => {
    clearTimeout(timer.current);
    state.current.text = '';
    state.current.dirty = false;
    setText('');
    clearCache();
    draftChanged();
    // Preserve a teammate's newer draft if it changed while the reviewed message was being sent.
    await load();
  };

  return {
    text, status, change, save, discard, sent,
    revision: () => state.current.revision,
    reload: async () => { clearCache(); state.current.dirty = false; state.current.conflict = false; await load(); },
    retryLoad: () => load(true),
  };
}
